import { CircularProgress, Stack, ToggleButton } from '@montugroup/design-system';
import type { WaitlistAvailability } from '@montugroup/pms-api-contracts';
import { FormControl } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { ErrorAlert } from '@/components/common/RebrandStyledComponents';
import { DEFAULT_WAITLIST_AVAILABILITY, WAITLIST_AVAILABILITY_OPTIONS } from './waitlist.config';
import {
  ActionButton,
  AvailabilityLabel,
  AvailabilityToggleGroup,
  FormActions,
  HelperText,
} from './WaitlistPreferenceForm.styles';

export interface WaitlistPreferenceFormProps {
  initialAvailability: WaitlistAvailability | null;
  submitLabel: string;
  cancelLabel?: string;
  isSubmitting: boolean;
  errorMessage: string | null;
  onSubmit: (availability: WaitlistAvailability) => void;
  onCancel: () => void;
}

export function WaitlistPreferenceForm({
  initialAvailability,
  submitLabel,
  cancelLabel = 'Cancel',
  isSubmitting,
  errorMessage,
  onSubmit,
  onCancel,
}: WaitlistPreferenceFormProps) {
  const [availability, setAvailability] = useState<WaitlistAvailability>(
    initialAvailability ?? DEFAULT_WAITLIST_AVAILABILITY,
  );
  const selectedOption = WAITLIST_AVAILABILITY_OPTIONS.find((option) => option.value === availability);
  const firstFieldRef = useRef<HTMLFieldSetElement>(null);

  // move focus into the form when it opens, so keyboard and screen reader users land on it
  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  return (
    <Stack
      component="form"
      gap={6}
      width="100%"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit(availability);
      }}
    >
      <FormControl component="fieldset" ref={firstFieldRef} tabIndex={-1} fullWidth sx={{ outline: 'none' }}>
        <AvailabilityLabel component="legend">When are you available?</AvailabilityLabel>
        <AvailabilityToggleGroup
          exclusive
          fullWidth
          orientation="vertical"
          value={availability}
          // a click on the chosen option gives null; ignore it so there's always a value
          onChange={(_, value: WaitlistAvailability | null) => {
            if (value) {
              setAvailability(value);
            }
          }}
        >
          {WAITLIST_AVAILABILITY_OPTIONS.map(({ value, label }) => (
            <ToggleButton key={value} value={value}>
              {label}
            </ToggleButton>
          ))}
        </AvailabilityToggleGroup>
        <HelperText mt={2}>{selectedOption?.description}</HelperText>
      </FormControl>
      <HelperText>
        We&apos;ll text offers to your mobile. Reply YES to accept; your current booking is cancelled automatically.
      </HelperText>
      {errorMessage && <ErrorAlert severity="error">{errorMessage}</ErrorAlert>}
      <FormActions>
        <ActionButton
          type="submit"
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitLabel}
        </ActionButton>
        <ActionButton type="button" variant="outlined" onClick={onCancel} disabled={isSubmitting}>
          {cancelLabel}
        </ActionButton>
      </FormActions>
    </Stack>
  );
}
